import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchAppointments,
  createAppointment,
  updateAppointment,
  deleteAppointment,
  fetchPatients,
  fetchDoctors,
} from "../features/appointments/calendaryThunks.js";

import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import PageHeader from "./PageHeader.jsx";
import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop";
import { format, parse, startOfWeek, getDay, addMinutes } from "date-fns";
import es from "date-fns/locale/es";

import {
  CalendarDays,
  Clock,
  Stethoscope,
  CheckCircle2,
  XCircle,
  ChevronDown,
  X,
} from "lucide-react";

import "react-big-calendar/lib/css/react-big-calendar.css";
import "react-big-calendar/lib/addons/dragAndDrop/styles.css";
import "../styles/calendary.css";

const locales = { es };
const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 1 }),
  getDay,
  locales,
});
const DnDCalendar = withDragAndDrop(Calendar);

const hourToDate = (h) => new Date(2020, 0, 1, h, 0, 0);
const MINUTES_SLOT = 30;
const MIN_DURATION_MIN = 60;

const pad2 = (n) => String(n).padStart(2, "0");
const toISODate = (d) => {
  const dt = new Date(d);
  return `${dt.getFullYear()}-${pad2(dt.getMonth() + 1)}-${pad2(dt.getDate())}`;
};
const toHHmm = (d) => `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;

const roundToStep = (date, step = MINUTES_SLOT) => {
  const d = new Date(date);
  d.setSeconds(0, 0);
  const m = d.getMinutes();
  const snap = Math.round(m / step) * step;
  if (snap >= 60) {
    d.setHours(d.getHours() + 1);
    d.setMinutes(0);
  } else {
    d.setMinutes(snap);
  }
  return d;
};

const ensureMinDuration = (start, end, minMinutes = MIN_DURATION_MIN) => {
  const s = new Date(start);
  let e = new Date(end);
  if (e <= s || (e - s) / 60000 < minMinutes) e = addMinutes(s, minMinutes);
  return { start: s, end: e };
};

const combineDateTime = (dateStr, timeStr) => {
  const [y, m, d] = dateStr.split("-").map(Number);
  const [h, mm] = timeStr.split(":").map(Number);
  return new Date(y, m - 1, d, h, mm, 0, 0);
};

const normalizeStatus = (status) => {
  const s = String(status || "").toLowerCase();
  if (s === "pending") return "planned";
  if (s === "confirmed") return "finished";
  if (s === "cancelled") return "cancelled";
  if (["planned", "waiting_room", "in_consultation", "finished", "cancelled"].includes(s)) return s;
  return "planned";
};

const STATUS_OPTIONS = [
  { value: "planned", label: "Planificada", Icon: CalendarDays },
  { value: "waiting_room", label: "Sala de espera", Icon: Clock },
  { value: "in_consultation", label: "En consulta", Icon: Stethoscope },
  { value: "finished", label: "Finalizada", Icon: CheckCircle2 },
  { value: "cancelled", label: "Cancelada", Icon: XCircle },
];

/* ✅ Área + Motivo (opciones en frontend) */
const AREA_OPTIONS = [
  { value: "administrative", label: "Administrativo" },
  { value: "intervention", label: "Intervención" },
  { value: "follow_up", label: "Seguimiento" },
];

const VISIT_REASON_OPTIONS_BY_AREA = {
  administrative: [
    { value: "administrative_budget", label: "Presupuesto" },
    { value: "administrative_documentation_consents", label: "Documentación / Consentimientos" },
    { value: "administrative_red_flag", label: "Red Flag" },
    { value: "administrative_other", label: "Otro" },
  ],
  intervention: [
    { value: "intervention_first_visit", label: "Primera Visita" },
    { value: "intervention_preoperative", label: "Preoperatorio" },
    { value: "intervention_admission", label: "Ingreso" },
    { value: "intervention_red_flag", label: "Red Flag" },
    { value: "intervention_other", label: "Otro" },
  ],
  follow_up: [
    { value: "follow_up_review_1m", label: "Revisión 1 mes" },
    { value: "follow_up_review_3m", label: "Revisión 3 meses" },
    { value: "follow_up_review_6m", label: "Revisión 6 meses" },
    { value: "follow_up_review_9m", label: "Revisión 9 meses" },
    { value: "follow_up_red_flag", label: "Red Flag" },
  ],
};

const inferAreaFromVisitReason = (visitReasonValue) => {
  const value = String(visitReasonValue || "");
  if (value.startsWith("administrative_")) return "administrative";
  if (value.startsWith("intervention_")) return "intervention";
  if (value.startsWith("follow_up_")) return "follow_up";
  return "";
};

const getVisitReasonLabel = (areaValue, visitReasonValue) => {
  const area = areaValue || inferAreaFromVisitReason(visitReasonValue);
  const list = VISIT_REASON_OPTIONS_BY_AREA[area] || [];
  const found = list.find((r) => r.value === visitReasonValue);
  return found ? found.label : visitReasonValue ? String(visitReasonValue) : "";
};

const formatBirthDate = (value) => {
  if (!value) return "—";
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return String(value);
  return format(dt, "dd/MM/yyyy");
};

/* HEADER */
const HeaderBar = ({ canCreate, onCreate }) => {
  return (
    <div className="header-bar">
      <div className="header-title"><PageHeader title="Calendario" /></div>
      {canCreate && (
        <button className="btn-primary" onClick={onCreate}>
          Crear Cita
        </button>
      )}
    </div>
  );
};

/* TOOLBAR */
const ToolBar = ({ label, onNavigate, onView, view }) => {
  return (
    <div className="toolbar">
      <div className="toolbar-left">
        <button className="ghost" onClick={() => onNavigate("PREV")} aria-label="Anterior">
          ‹
        </button>
        <div className="month-label">{label}</div>
        <button className="ghost" onClick={() => onNavigate("NEXT")} aria-label="Siguiente">
          ›
        </button>
        <button className="ghost" onClick={() => onNavigate("TODAY")}>
          Hoy
        </button>
      </div>
      <div className="toolbar-right">
        <div className="view-switch">
          {["day", "week", "month"].map((v) => (
            <button key={v} className={`seg ${view === v ? "active" : ""}`} onClick={() => onView(v)}>
              {v === "day" && "Día"}
              {v === "week" && "Semana"}
              {v === "month" && "Mes"}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

/* RENDER CITA EN SEMANA */
const EventCard = ({ event }) => {
  const status = normalizeStatus(event.status);
  const name = event.title || "Cita";

  const area = event.raw?.area || inferAreaFromVisitReason(event.raw?.visit_reason);
  const visitReasonLabel = getVisitReasonLabel(area, event.raw?.visit_reason);

  const notes = event.raw?.reason || "";
  const start = format(new Date(event.start), "HH:mm");
  const end = format(new Date(event.end), "HH:mm");

  return (
    <div className="ev-card" data-status={status}>
      <div className="ev-row">
        <span className="dot" />
        <span className="ev-title">{name}</span>
      </div>
      {visitReasonLabel ? <div className="ev-sub">{visitReasonLabel}</div> : null}
      {notes ? <div className="ev-sub" style={{ opacity: 0.75 }}>{notes}</div> : null}
      <div className="ev-time">
        {start} – {end}
      </div>
    </div>
  );
};

const eventPropGetter = (event) => ({
  className: `ev ev--${normalizeStatus(event.status)}`,
});

/* RENDER CITAS EN MES */
const makeMonthDateHeader = (countsByDay) => {
  return function MonthDateHeader({ label, date }) {
    const key = new Date(date).toDateString();
    const count = countsByDay[key] || 0;

    return (
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <span>{label}</span>
        {count > 0 && (
          <span
            style={{
              marginLeft: "auto",
              fontSize: 11,
              background: "#f6f0fa",
              border: "1px solid rgba(91,55,104,0.12)",
              padding: "2px 6px",
              borderRadius: 10,
              color: "#3a2b36",
              fontWeight: 700,
            }}
          >
            {count} cita{count !== 1 ? "s" : ""}
          </span>
        )}
      </div>
    );
  };
};

const StatusSelect = ({ value, onChange, disabled }) => {
  const [open, setOpen] = useState(false);
  const [placement, setPlacement] = useState("down");
  const rootRef = useRef(null);

  const MENU_MAX_HEIGHT = 260;

  useEffect(() => {
    const handleClickOutside = (ev) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(ev.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!open) return;
    if (!rootRef.current) return;

    const rect = rootRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;

    const shouldOpenUp = spaceBelow < MENU_MAX_HEIGHT && spaceAbove > spaceBelow;
    setPlacement(shouldOpenUp ? "up" : "down");
  }, [open]);

  const current = useMemo(() => {
    const v = normalizeStatus(value);
    return STATUS_OPTIONS.find((o) => o.value === v) || STATUS_OPTIONS[0];
  }, [value]);

  const CurrentIcon = current.Icon;

  return (
    <div
      ref={rootRef}
      className={`status-select ${disabled ? "is-disabled" : ""} ${placement === "up" ? "is-up" : ""}`}
    >
      <button
        type="button"
        className="status-select-trigger"
        onClick={() => {
          if (disabled) return;
          setOpen((p) => !p);
        }}
        disabled={disabled}
      >
        <span className="status-select-left">
          <CurrentIcon size={16} />
          <span>{current.label}</span>
        </span>
        <ChevronDown size={16} />
      </button>

      {open ? (
        <div className="status-select-menu" role="listbox">
          {STATUS_OPTIONS.map((opt) => {
            const ItemIcon = opt.Icon;
            const active = opt.value === current.value;

            return (
              <button
                key={opt.value}
                type="button"
                className={`status-select-item ${active ? "is-active" : ""}`}
                onClick={() => {
                  onChange(opt.value);
                  setOpen(false);
                }}
              >
                <ItemIcon size={16} />
                <span>{opt.label}</span>
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
};

/* FORMULARIO CREAR / EDITAR CITA */
const AppointmentModal = ({
  open,
  onClose,
  onSubmit,
  initialValues = {},
  doctorUser,
  doctors = [],
  patients = [],
  canEditDoctor = false,
  canEditStatus = true,
  showDelete = false,
  onRequestDelete,
}) => {
  const [formData, setFormData] = useState(() => {
    const startInit = initialValues.start ? new Date(initialValues.start) : new Date();
    const endInit = initialValues.end ? new Date(initialValues.end) : addMinutes(startInit, MIN_DURATION_MIN);

    const initialArea =
      initialValues.area || inferAreaFromVisitReason(initialValues.visitReason || initialValues.visit_reason || "");
    const initialVisitReason = initialValues.visitReason || initialValues.visit_reason || "";

    return {
      doctorId: canEditDoctor ? (initialValues.doctorId ?? "") : doctorUser?.id ?? initialValues.doctorId ?? "",
      patientId: initialValues.patientId ?? "",
      date: toISODate(startInit),
      time: toHHmm(startInit),
      endTime: toHHmm(endInit),
      area: initialArea || "",
      visitReason: initialVisitReason || "",
      notes: initialValues.notes ?? initialValues.reason ?? "",
      status: normalizeStatus(initialValues.status || "planned"),
    };
  });

  useEffect(() => {
    if (!open) return;

    const startInit = initialValues.start ? new Date(initialValues.start) : new Date();
    const endInit = initialValues.end ? new Date(initialValues.end) : addMinutes(startInit, MIN_DURATION_MIN);

    const initialArea =
      initialValues.area || inferAreaFromVisitReason(initialValues.visitReason || initialValues.visit_reason || "");
    const initialVisitReason = initialValues.visitReason || initialValues.visit_reason || "";

    setFormData({
      doctorId: canEditDoctor ? (initialValues.doctorId ?? "") : doctorUser?.id ?? initialValues.doctorId ?? "",
      patientId: initialValues.patientId ?? "",
      date: toISODate(startInit),
      time: toHHmm(startInit),
      endTime: toHHmm(endInit),
      area: initialArea || "",
      visitReason: initialVisitReason || "",
      notes: initialValues.notes ?? initialValues.reason ?? "",
      status: normalizeStatus(initialValues.status || "planned"),
    });
  }, [
    open,
    initialValues?.id,
    initialValues?.start,
    initialValues?.end,
    initialValues?.doctorId,
    initialValues?.patientId,
    initialValues?.area,
    initialValues?.visitReason,
    initialValues?.visit_reason,
    initialValues?.reason,
    initialValues?.notes,
    initialValues?.status,
    canEditDoctor,
    doctorUser?.id,
  ]);

  const handleChange = (key, value) =>
    setFormData((prev) => ({
      ...prev,
      [key]: value,
    }));

  const doctorOptions = useMemo(() => {
    return (doctors || []).map((d) => ({
      id: d.id,
      name: `${d.first_name || ""} ${d.last_name || ""}`.trim() || d.username || `#${d.id}`,
      username: d.username,
    }));
  }, [doctors]);

  const selectedPatient = useMemo(() => {
    const selectedId = Number(formData.patientId);
    if (!selectedId) return null;
    return (patients || []).find((p) => Number(p.id) === selectedId) || null;
  }, [patients, formData.patientId]);

  const visitReasonOptions = useMemo(() => {
    if (!formData.area) return [];
    return VISIT_REASON_OPTIONS_BY_AREA[formData.area] || [];
  }, [formData.area]);

  const handleAreaChange = (nextArea) => {
    setFormData((prev) => ({
      ...prev,
      area: nextArea,
      visitReason: "",
      doctorId: nextArea === "administrative" && canEditDoctor ? "" : prev.doctorId,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const start = roundToStep(combineDateTime(formData.date, formData.time));
    const end = roundToStep(combineDateTime(formData.date, formData.endTime));
    const { end: fixedEnd } = ensureMinDuration(start, end);

    const doctorIdValue = canEditDoctor
      ? formData.doctorId
        ? Number(formData.doctorId)
        : null
      : doctorUser?.id ?? initialValues.doctorId ?? null;

    const patientIdValue = formData.patientId ? Number(formData.patientId) : null;

    onSubmit({
      id: initialValues.id ?? null,
      doctorId: doctorIdValue,
      patientId: patientIdValue,
      start,
      end: fixedEnd,
      area: formData.area,
      visitReason: formData.visitReason,
      notes: String(formData.notes || "").trim(),
      status: normalizeStatus(formData.status || "planned"),
    });
  };

  if (!open) return null;

  const doctorIsRequired = formData.area !== "administrative"; // ✅

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h3>{initialValues?.id ? "Editar cita" : "Nueva cita"}</h3>

        <form onSubmit={handleSubmit}>
          {/* Doctor */}
          <div className="field">
            <label>Doctor {formData.area === "administrative" ? "(opcional)" : ""}</label>

            {canEditDoctor ? (
              <select
                value={formData.doctorId}
                onChange={(e) => handleChange("doctorId", e.target.value)}
                required={doctorIsRequired}
              >
                <option value="">
                  {formData.area === "administrative" ? "Sin asignar" : "Selecciona..."}
                </option>
                {doctorOptions.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            ) : (
              <>
                <input type="hidden" name="doctor_id" value={formData.doctorId} />
                <div className="readonly-pill">
                  {doctorUser
                    ? `${doctorUser.first_name} ${doctorUser.last_name}`.trim()
                    : formData.doctorId
                    ? `Doctor #${formData.doctorId}`
                    : "Sin doctor"}
                </div>
              </>
            )}
          </div>

          {/* Paciente */}
          <div className="field">
            <label>Paciente</label>
            <select value={formData.patientId} onChange={(e) => handleChange("patientId", e.target.value)} required>
              <option value="">Selecciona...</option>
              {patients.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>

            {selectedPatient ? (
              <div className="patient-preview">
                <div className="patient-preview-row">
                  <span className="patient-preview-label">Móvil</span>
                  <span className="patient-preview-value">{selectedPatient.phone || "—"}</span>
                </div>
                <div className="patient-preview-row">
                  <span className="patient-preview-label">Email</span>
                  <span className="patient-preview-value">{selectedPatient.email || "—"}</span>
                </div>
                <div className="patient-preview-row">
                  <span className="patient-preview-label">Nacimiento</span>
                  <span className="patient-preview-value">{formatBirthDate(selectedPatient.birthDate)}</span>
                </div>
              </div>
            ) : null}
          </div>

          {/* Fecha */}
          <div className="field">
            <label>Fecha</label>
            <input type="date" value={formData.date} onChange={(e) => handleChange("date", e.target.value)} required />
          </div>

          {/* Hora inicio y fin */}
          <div className="inline-2">
            <div className="field">
              <label>Hora de inicio</label>
              <input
                type="time"
                step={1800}
                min="07:00"
                max="22:00"
                value={formData.time}
                onChange={(e) => handleChange("time", e.target.value)}
                required
              />
            </div>

            <div className="field">
              <label>Hora de finalización</label>
              <input
                type="time"
                step={1800}
                min="07:30"
                max="23:00"
                value={formData.endTime || ""}
                onChange={(e) => handleChange("endTime", e.target.value)}
                required
              />
            </div>
          </div>

          {/* Área */}
          <div className="field">
            <label>Área</label>
            <select value={formData.area} onChange={(e) => handleAreaChange(e.target.value)} required>
              <option value="">Selecciona...</option>
              {AREA_OPTIONS.map((a) => (
                <option key={a.value} value={a.value}>
                  {a.label}
                </option>
              ))}
            </select>
          </div>

          {/* Motivo */}
          <div className="field">
            <label>Motivo</label>
            <select
              value={formData.visitReason}
              onChange={(e) => handleChange("visitReason", e.target.value)}
              required
              disabled={!formData.area}
            >
              <option value="">{formData.area ? "Selecciona..." : "Selecciona un área primero"}</option>
              {visitReasonOptions.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>

          {/* Observaciones */}
          <div className="field">
            <label>Observaciones (opcional)</label>
            <textarea
              rows={3}
              value={formData.notes}
              onChange={(e) => handleChange("notes", e.target.value)}
              placeholder="Notas internas..."
            />
          </div>

          {/* Estado */}
          <div className="field">
            <label>Estado</label>
            <StatusSelect value={formData.status} onChange={(next) => handleChange("status", next)} disabled={!canEditStatus} />
          </div>

          <div className="modal-actions">
            {showDelete && (
              <button type="button" className="btn-danger btn-sm" onClick={onRequestDelete}>
                Eliminar
              </button>
            )}
            <button type="button" className="btn-secondary btn-sm" onClick={onClose}>
              Cancelar
            </button>
            <button type="submit" className="btn-primary">
              Guardar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const EmptyMonthEvent = () => null;

const CalendarBoard = React.memo(function CalendarBoard({
  events,
  view,
  setView,
  isPatient,
  onSelectSlot,
  onSelectEvent,
  MonthDateHeader,
  draggableAccessor,
  resizableAccessor,
  onEventDrop,
  onEventResize,
}) {
  const calendarComponents = useMemo(() => {
    return {
      toolbar: ToolBar,
      event: EventCard,
      month: { dateHeader: MonthDateHeader, event: EmptyMonthEvent },
    };
  }, [MonthDateHeader]);

  return (
    <DnDCalendar
      localizer={localizer}
      culture="es"
      events={events}
      startAccessor="start"
      endAccessor="end"
      view={view}
      onView={setView}
      views={["day", "week", "month"]}
      defaultView="week"
      step={MINUTES_SLOT}
      timeslots={2}
      selectable={!isPatient && view !== "month"}
      onSelectSlot={onSelectSlot}
      onSelectEvent={onSelectEvent}
      eventPropGetter={eventPropGetter}
      components={calendarComponents}
      popup
      min={hourToDate(7)}
      max={hourToDate(22)}
      style={{ height: "100vh" }}
      draggableAccessor={draggableAccessor}
      resizable
      resizableAccessor={resizableAccessor}
      onEventDrop={onEventDrop}
      onEventResize={onEventResize}
    />
  );
});

const Calendary = () => {
  const dispatch = useDispatch();

  const { appointments, patients, doctors, loading, error } = useSelector((state) => state.calendary);
  const { user } = useSelector((state) => state.auth);

  const role = user?.role;
  const isAdminOrReception = role === "admin" || role === "receptionist";
  const isDoctor = role === "doctor";
  const isPatient = role === "patient";

  const [view, setView] = useState("week");

  // Optimistic UI para drag/resize
  const [optimisticTimesById, setOptimisticTimesById] = useState({});
  const setOptimisticTimes = useCallback((appointmentId, startIso, endIso) => {
    setOptimisticTimesById((prev) => ({
      ...prev,
      [appointmentId]: { start_time: startIso, end_time: endIso },
    }));
  }, []);
  const clearOptimisticTimes = useCallback((appointmentId) => {
    setOptimisticTimesById((prev) => {
      if (!prev[appointmentId]) return prev;
      const next = { ...prev };
      delete next[appointmentId];
      return next;
    });
  }, []);

  useEffect(() => {
    dispatch(fetchAppointments());
    dispatch(fetchPatients());
  }, [dispatch]);

  useEffect(() => {
    if (isAdminOrReception) dispatch(fetchDoctors());
  }, [dispatch, isAdminOrReception]);

  const countsByDay = useMemo(() => {
    const map = {};
    (appointments || []).forEach((a) => {
      const key = new Date(a.start_time).toDateString();
      map[key] = (map[key] || 0) + 1;
    });
    return map;
  }, [appointments]);

  const MonthDateHeader = useMemo(() => makeMonthDateHeader(countsByDay), [countsByDay]);

  const canEditEvent = useCallback(
    (ev) => {
      if (isPatient) return false;
      if (isDoctor) {
        const doctorId = Number(ev?.raw?.doctor?.id ?? ev?.doctorId ?? ev?.doctor?.id);
        return doctorId === Number(user?.id);
      }
      return true;
    },
    [isPatient, isDoctor, user]
  );

  const events = useMemo(() => {
    return (appointments || []).map((a) => {
      const override = optimisticTimesById[a.id];
      const startTime = override?.start_time || a.start_time;
      const endTime = override?.end_time || a.end_time;

      return {
        id: a.id,
        title: a?.patient
          ? `${a.patient.first_name || ""} ${a.patient.last_name || ""}`.trim() || "Cita"
          : "Cita",
        start: new Date(startTime),
        end: new Date(endTime),
        status: normalizeStatus(a.status),
        raw: a,
      };
    });
  }, [appointments, optimisticTimesById]);

  const patientOptions = useMemo(() => {
    return (patients || []).map((p) => ({
      id: p.id,
      name: `${p.first_name || ""} ${p.last_name || ""}`.trim() || p.username || `#${p.id}`,
      phone: p.phone || "",
      email: p.email || "",
      birthDate: p.birth_date || null,
    }));
  }, [patients]);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  // Delete confirm modal (warning)
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [appointmentToDelete, setAppointmentToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [reopenEditAfterDeleteCancel, setReopenEditAfterDeleteCancel] = useState(false);

  const openCreateNow = useCallback(() => {
    if (isPatient) return;
    if (view === "month") return;

    const start = roundToStep(new Date());
    const { end } = ensureMinDuration(start, addMinutes(start, MIN_DURATION_MIN));

    setEditing({
      id: null,
      title: "",
      doctorId: isDoctor ? user.id : "",
      patientId: "",
      start,
      end,
      area: "",
      visitReason: "",
      notes: "",
      status: "planned",
    });

    setOpen(true);
  }, [isPatient, isDoctor, user, view]);

  const onSelectSlot = useCallback(
    ({ start, end }) => {
      if (isPatient) return;
      if (view === "month") return;

      const snappedStart = roundToStep(start);
      const adjusted = ensureMinDuration(snappedStart, end || addMinutes(snappedStart, MIN_DURATION_MIN));

      setEditing({
        id: null,
        title: "",
        doctorId: isDoctor ? user.id : "",
        patientId: "",
        start: adjusted.start,
        end: adjusted.end,
        area: "",
        visitReason: "",
        notes: "",
        status: "planned",
      });

      setOpen(true);
    },
    [isPatient, isDoctor, user, view]
  );

  const onSelectEvent = useCallback(
    (event) => {
      if (!canEditEvent(event)) return;

      const existingDoctorId = event.raw?.doctor?.id ? Number(event.raw.doctor.id) : "";

      setEditing({
        id: event.id,
        title: event.title || "Cita",
        doctorId: isDoctor ? user.id : existingDoctorId,
        patientId: Number(event.raw?.patient?.id ?? 0),
        start: new Date(event.start),
        end: new Date(event.end),
        area: event.raw?.area || inferAreaFromVisitReason(event.raw?.visit_reason),
        visitReason: event.raw?.visit_reason || "",
        notes: event.raw?.reason || "",
        status: normalizeStatus(event.status),
      });

      setOpen(true);
    },
    [canEditEvent, isDoctor, user]
  );

  const closeModal = useCallback(() => setOpen(false), []);

  const submitAppointment = useCallback(
    async (data) => {
      const payloadBase = {
        start_time: data.start.toISOString(),
        end_time: data.end.toISOString(),
        status: normalizeStatus(data.status),
        area: data.area,
        visit_reason: data.visitReason,
        reason: data.notes || "",
      };

      if (!data.id) {
        if (isDoctor) {
          await dispatch(createAppointment({ ...payloadBase, patient_id: data.patientId }));
        } else {
          const createPayload = {
            ...payloadBase,
            patient_id: data.patientId,
            doctor_id: data.area === "administrative" ? (data.doctorId ?? null) : data.doctorId,
          };
          await dispatch(createAppointment(createPayload));
        }
      } else {
        if (isDoctor) {
          await dispatch(updateAppointment({ id: data.id, appointmentData: { ...payloadBase } }));
        } else {
          const updatePayload = {
            ...payloadBase,
            patient_id: data.patientId,
            doctor_id: data.area === "administrative" ? (data.doctorId ?? null) : data.doctorId,
          };
          await dispatch(updateAppointment({ id: data.id, appointmentData: updatePayload }));
        }
      }

      setOpen(false);
    },
    [dispatch, isDoctor]
  );

  const requestDelete = useCallback(() => {
    if (!editing?.id) return;

    setDeleteError(null);
    setAppointmentToDelete({
      id: editing.id,
      title: editing.title || "Cita",
      start: editing.start,
      end: editing.end,
    });

    setReopenEditAfterDeleteCancel(true);
    setDeleteOpen(true);
    setOpen(false);
  }, [editing]);

  const closeDelete = useCallback(() => {
    if (deleting) return;

    setDeleteOpen(false);
    setDeleteError(null);
    setAppointmentToDelete(null);

    if (reopenEditAfterDeleteCancel) setOpen(true);
    setReopenEditAfterDeleteCancel(false);
  }, [deleting, reopenEditAfterDeleteCancel]);

  const confirmDelete = useCallback(async () => {
    if (!appointmentToDelete?.id) return;

    setDeleteError(null);
    setDeleting(true);

    const result = await dispatch(deleteAppointment(appointmentToDelete.id));

    setDeleting(false);

    if (deleteAppointment.fulfilled.match(result)) {
      setDeleteOpen(false);
      setAppointmentToDelete(null);
      setReopenEditAfterDeleteCancel(false);
      setEditing(null);
      return;
    }

    setDeleteError(result.payload || "No se pudo eliminar la cita.");
  }, [dispatch, appointmentToDelete]);

  // UX: ESC + lock scroll si hay modal
  useEffect(() => {
    const hasModal = open || deleteOpen;

    const handleKeyDown = (event) => {
      if (event.key !== "Escape") return;
      if (deleteOpen) closeDelete();
      else if (open) closeModal();
    };

    if (hasModal) {
      document.addEventListener("keydown", handleKeyDown);
      const previousOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";

      return () => {
        document.removeEventListener("keydown", handleKeyDown);
        document.body.style.overflow = previousOverflow;
      };
    }

    return undefined;
  }, [open, deleteOpen, closeModal, closeDelete]);

  const saveEventTimeChange = useCallback(
    async ({ event, start, end }) => {
      if (!canEditEvent(event)) return;
      if (view === "month") return;

      const snappedStart = roundToStep(start);
      const snappedEnd = roundToStep(end || addMinutes(snappedStart, MIN_DURATION_MIN));
      const { start: s, end: e } = ensureMinDuration(snappedStart, snappedEnd);

      const appointmentId = event.id;
      const startIso = s.toISOString();
      const endIso = e.toISOString();

      setOptimisticTimes(appointmentId, startIso, endIso);

      try {
        await dispatch(
          updateAppointment({
            id: appointmentId,
            appointmentData: {
              start_time: startIso,
              end_time: endIso,
              status: normalizeStatus(event.raw?.status || event.status),
              area: event.raw?.area || inferAreaFromVisitReason(event.raw?.visit_reason),
              visit_reason: event.raw?.visit_reason || "",
              reason: event.raw?.reason || "",
            },
          })
        ).unwrap();

        clearOptimisticTimes(appointmentId);
      } catch (err) {
        clearOptimisticTimes(appointmentId);
      }
    },
    [dispatch, canEditEvent, view, setOptimisticTimes, clearOptimisticTimes]
  );

  const onEventDrop = useCallback((args) => saveEventTimeChange(args), [saveEventTimeChange]);
  const onEventResize = useCallback((args) => saveEventTimeChange(args), [saveEventTimeChange]);

  const draggableAccessor = useCallback((event) => view !== "month" && canEditEvent(event), [view, canEditEvent]);
  const resizableAccessor = useCallback((event) => view !== "month" && canEditEvent(event), [view, canEditEvent]);

  const showLoading = Boolean(loading && (!appointments || appointments.length === 0));

  return (
    <div className="agenda-shell">
      <HeaderBar canCreate={!isPatient} onCreate={openCreateNow} />

      <div className="calendar-card">
        <CalendarBoard
          events={events}
          view={view}
          setView={setView}
          isPatient={isPatient}
          onSelectSlot={onSelectSlot}
          onSelectEvent={onSelectEvent}
          MonthDateHeader={MonthDateHeader}
          draggableAccessor={draggableAccessor}
          resizableAccessor={resizableAccessor}
          onEventDrop={onEventDrop}
          onEventResize={onEventResize}
        />
      </div>

      <AppointmentModal
        open={open}
        onClose={closeModal}
        onSubmit={submitAppointment}
        initialValues={editing || {}}
        doctorUser={isDoctor ? user : null}
        doctors={isAdminOrReception ? doctors : []}
        patients={patientOptions}
        canEditDoctor={isAdminOrReception}
        canEditStatus={isAdminOrReception || isDoctor}
        showDelete={Boolean(editing?.id) && (isAdminOrReception || isDoctor)}
        onRequestDelete={requestDelete}
      />

      {deleteOpen && (
        <div className="ui-modal-overlay" onClick={closeDelete} role="presentation">
          <div
            className="ui-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Eliminar cita"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="ui-modal-header">
              <h3>Eliminar cita</h3>
              <button
                type="button"
                className="ghost btn-xs"
                onClick={closeDelete}
                disabled={deleting}
                aria-label="Cerrar"
                title="Cerrar"
              >
                <X width={16} height={16} aria-hidden="true" />
              </button>
            </div>

            <div className="ui-modal-body ui-modal-body--single">
              <div className="ui-delete-help">
                ¿Seguro que quieres eliminar{" "}
                <strong>{appointmentToDelete?.title || `Cita #${appointmentToDelete?.id}`}</strong>?
                <div className="ui-delete-subhelp">
                  {appointmentToDelete?.start
                    ? `Fecha: ${format(new Date(appointmentToDelete.start), "dd/MM/yyyy")} · ${format(
                        new Date(appointmentToDelete.start),
                        "HH:mm"
                      )} – ${format(new Date(appointmentToDelete.end), "HH:mm")}`
                    : "Esta acción no se puede deshacer."}
                </div>
              </div>

              {deleteError ? <div className="ui-modal-error">{deleteError}</div> : null}
            </div>

            <div className="ui-modal-footer">
              <button type="button" className="ghost btn-sm" onClick={closeDelete} disabled={deleting}>
                Cancelar
              </button>

              <button type="button" className="btn-danger btn-sm" onClick={confirmDelete} disabled={deleting}>
                {deleting ? "Eliminando…" : "Eliminar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showLoading ? <div style={{ marginTop: 8 }}>Cargando…</div> : null}
      {error ? <div style={{ marginTop: 8, color: "#b91c1c" }}>{error}</div> : null}
    </div>
  );
};

export default Calendary;